import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, ExternalLink, FileText, Eye, Edit2, Check, X, CheckCircle, Clock, AlertTriangle, BookOpen, FileDown, Hammer, Users, Briefcase, HardHat, Building2, Home, MessageSquare, Workflow, Tag, Filter, CheckSquare, Square } from 'lucide-react';

const CATEGORY_ICONS = {
  client:      Users,
  appraisal:   FileText,
  job_work:    Hammer,
  contractor:  HardHat,
  office:      Briefcase,
  property:    Home,
  testimonial: MessageSquare,
};

const STATUS_BADGES = {
  live: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Live' },
  wip:  { color: 'bg-amber-50 text-amber-700 border-amber-200',       icon: Clock,        label: 'WIP' },
  planned: { color: 'bg-slate-50 text-slate-500 border-slate-200',    icon: AlertTriangle, label: 'Planned' },
};

// Per-lifecycle-status chip colours — picked to match the badges already
// used on the Appraisal Detail page so the operator sees the same
// visual vocabulary in both places.
const LIFECYCLE_COLORS = {
  'Appraisal Required':  'bg-rose-50 text-rose-700 border-rose-200',
  'Appraisal Booked':    'bg-orange-50 text-orange-700 border-orange-200',
  'Appraisal Completed': 'bg-amber-50 text-amber-700 border-amber-200',
  'Quoted':              'bg-blue-50 text-blue-700 border-blue-200',
  'Quote Sent':          'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Accepted':            'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Declined':            'bg-slate-100 text-slate-500 border-slate-200',
  'Work Booked':         'bg-teal-50 text-teal-700 border-teal-200',
  'Work In Progress':    'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Completed':           'bg-green-50 text-green-700 border-green-200',
  'Invoiced':            'bg-violet-50 text-violet-700 border-violet-200',
  'Paid':                'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  'Closed':              'bg-slate-100 text-slate-600 border-slate-200',
};

function StatusChip({ value }) {
  const cls = LIFECYCLE_COLORS[value] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <span data-testid={`status-chip-${value.toLowerCase().replace(/\s+/g, '-')}`}
          className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      {value}
    </span>
  );
}

const ACCEPT_TYPES = '.pdf,.doc,.docx,.rtf,.txt,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,.bmp,.xls,.xlsx,.csv,.html,.htm,.accdb,.mdb,.rdl';

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ReportsLibraryPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ categories: [], lifecycle_statuses: [] });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('appraisal');
  const [statusFilter, setStatusFilter] = useState('');   // '' = all
  // Per-category "focused report" — when set, the samples panel only shows
  // samples linked to that built-in. Empty = show all (legacy behaviour).
  const [reportFilter, setReportFilter] = useState({});   // { [catKey]: report_key }
  const [selectedSamples, setSelectedSamples] = useState({});  // { [sampleId]: true }
  const [uploadingFor, setUploadingFor] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatuses, setEditStatuses] = useState([]);
  const fileInputRefs = useRef({});

  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/reports-library');
      setData(d);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (category, file, opts = {}) => {
    setUploadingFor(category);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      fd.append('notes', opts.notes || '');
      if (statusFilter) fd.append('statuses', statusFilter);
      // When the operator has focused on a specific built-in report, auto-link
      // the uploaded sample to it so the click-filter view picks it up.
      const focused = opts.report_key !== undefined ? opts.report_key : reportFilter[category];
      if (focused) fd.append('report_key', focused);
      await api.post('/reports-library/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (e) {
      alert('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
    setUploadingFor('');
  };

  const toggleSelect = (id) => {
    setSelectedSamples(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const selectAllVisible = (samples) => {
    setSelectedSamples(prev => {
      const next = { ...prev };
      const allOn = samples.every(s => next[s._id]);
      samples.forEach(s => {
        if (allOn) delete next[s._id];
        else next[s._id] = true;
      });
      return next;
    });
  };

  const bulkDelete = async () => {
    const ids = Object.keys(selectedSamples).filter(id => selectedSamples[id]);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} sample${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      await api.post('/reports-library/bulk-delete', { ids });
      setSelectedSamples({});
      await load();
    } catch (e) {
      alert('Bulk delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const onFilePick = (category, ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    // Sequentially upload — backend de-dupes by _id, but UI feels more "linear"
    (async () => {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await handleUpload(category, f);
      }
      ev.target.value = '';
    })();
  };

  const onDrop = (category, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const files = Array.from(ev.dataTransfer?.files || []);
    if (!files.length) return;
    (async () => {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await handleUpload(category, f);
      }
    })();
  };

  const onDragOver = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
  };

  const saveSampleEdits = async (id) => {
    try {
      await api.patch(`/reports-library/${id}`, { notes: editNotes, statuses: editStatuses });
      setEditingId('');
      setEditNotes('');
      setEditStatuses([]);
      await load();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const toggleEditStatus = (s) => {
    setEditStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const removeSample = async (id, filename) => {
    if (!window.confirm(`Delete sample "${filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/reports-library/${id}`);
      await load();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const testReport = (report) => {
    const id = report.test_id || '';
    if (report.viewer) {
      const path = report.viewer.replace('{id}', id);
      navigate(path);
      return;
    }
    if (report.url_template) {
      const url = `${apiBase}${report.url_template.replace('{id}', id)}`;
      window.open(url, '_blank', 'noopener');
      return;
    }
    alert('No live endpoint for this report yet.');
  };

  // Filtered view computed BEFORE any early returns so React Hook rules
  // (consistent call order on every render) are respected.
  const visibleCats = useMemo(() => {
    const cats = data.categories || [];
    if (!statusFilter) return cats;
    return cats.map(c => ({
      ...c,
      built_in: c.built_in.filter(r => !r.statuses || r.statuses.length === 0 || r.statuses.includes(statusFilter)),
      samples: c.samples.filter(s => !s.statuses || s.statuses.length === 0 || s.statuses.includes(statusFilter)),
    }));
  }, [data, statusFilter]);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading reports library…</div>;

  const cats = data.categories || [];
  const lifecycle = data.lifecycle_statuses || [];

  const totals = {
    builtIn: cats.reduce((acc, c) => acc + c.built_in.length, 0),
    live:    cats.reduce((acc, c) => acc + c.built_in.filter(r => r.status === 'live').length, 0),
    samples: cats.reduce((acc, c) => acc + c.samples.length, 0),
  };

  return (
    <div data-testid="reports-library-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
            <BookOpen size={26} className="text-[#D35400]" />
            Reports Library
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Central catalog of every report &amp; letter the CRM generates. Drag in your Access exports to use as references when we build new ones.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
          <div className="flex items-center gap-1.5"><CheckCircle size={14} className="text-emerald-600" /><span className="font-semibold text-slate-700">{totals.live}</span> live</div>
          <div className="flex items-center gap-1.5"><FileText size={14} className="text-slate-400" /><span className="font-semibold text-slate-700">{totals.builtIn}</span> total reports</div>
          <div className="flex items-center gap-1.5"><FileDown size={14} className="text-blue-600" /><span className="font-semibold text-slate-700">{totals.samples}</span> samples uploaded</div>
        </div>
      </div>

      {/* Lifecycle status filter — drives auto-generation later. */}
      <div data-testid="lifecycle-filter" className="bg-white border border-[#E2E2DF] rounded-md p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 pr-2 border-r border-[#E2E2DF]">
          <Workflow size={14} className="text-[#D35400]" />
          Filter by status
        </div>
        <button
          data-testid="status-filter-all"
          onClick={() => setStatusFilter('')}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === '' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-[#E2E2DF] hover:border-slate-300'}`}
        >
          All stages
        </button>
        {lifecycle.map(s => {
          const active = statusFilter === s;
          const cls = active
            ? 'bg-[#D35400] text-white border-[#D35400]'
            : (LIFECYCLE_COLORS[s] || 'bg-white text-slate-600 border-[#E2E2DF]') + ' hover:border-slate-300';
          return (
            <button
              key={s}
              data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${cls}`}
            >
              {s}
            </button>
          );
        })}
      </div>

      {/* Category jump-tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 border-b border-[#E2E2DF]">
        {visibleCats.map(c => {
          const Icon = CATEGORY_ICONS[c.key] || FileText;
          const isActive = activeCat === c.key;
          return (
            <button
              key={c.key}
              data-testid={`cat-tab-${c.key}`}
              onClick={() => {
                setActiveCat(c.key);
                document.getElementById(`section-${c.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${isActive ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
              <Icon size={14} />
              {c.label}
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.built_in.length}/{c.samples.length}</span>
            </button>
          );
        })}
      </div>

      {/* Sections */}
      {visibleCats.map(c => {
        const Icon = CATEGORY_ICONS[c.key] || FileText;
        return (
          <section
            key={c.key}
            id={`section-${c.key}`}
            data-testid={`section-${c.key}`}
            className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-3 bg-slate-50/60 border-b border-[#E2E2DF]">
              <div className="w-9 h-9 rounded-md bg-[#D35400]/10 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-[#D35400]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-slate-800 font-[Manrope]">{c.label}</h2>
                <p className="text-[11px] text-slate-500">
                  {c.built_in.length} built-in &middot; {c.samples.length} reference sample{c.samples.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-[#E2E2DF]">
              {/* LEFT — Built-in reports */}
              <div className="p-5 space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Built-in reports</h3>
                {c.built_in.length === 0 && (
                  <p className="text-xs italic text-slate-400">No built-in reports in this category yet.</p>
                )}
                {c.built_in.map(r => {
                  const badge = STATUS_BADGES[r.status] || STATUS_BADGES.planned;
                  const BadgeIcon = badge.icon;
                  const canTest = r.status === 'live' && (r.viewer || r.url_template) && r.test_id;
                  const isFocused = reportFilter[c.key] === r.key;
                  const sampleCount = c.samples.filter(s => s.report_key === r.key).length;
                  return (
                    <div
                      key={r.key}
                      data-testid={`builtin-${r.key}`}
                      onClick={() => setReportFilter(prev => ({ ...prev, [c.key]: isFocused ? '' : r.key }))}
                      className={`flex items-start gap-3 p-3 border rounded-md transition-colors group cursor-pointer ${isFocused ? 'border-[#D35400] bg-[#D35400]/5' : 'border-[#E2E2DF] hover:border-slate-300'}`}
                      title="Click to show only this report's reference samples"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">{r.label}</span>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.color}`}>
                            <BadgeIcon size={10} />
                            {badge.label}
                          </span>
                          {sampleCount > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">
                              <FileText size={10} />{sampleCount}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{r.description}</p>
                        {(r.statuses && r.statuses.length > 0) ? (
                          <div className="flex items-center gap-1 flex-wrap mt-1.5">
                            <Tag size={10} className="text-slate-400" />
                            {r.statuses.map(s => <StatusChip key={s} value={s} />)}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 mt-1.5">
                            <Tag size={10} className="text-slate-300" />
                            <span className="text-[10px] italic text-slate-400">All stages</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button
                          data-testid={`test-${r.key}`}
                          onClick={(e) => { e.stopPropagation(); testReport(r); }}
                          disabled={!canTest}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-[#D35400] border border-[#D35400] rounded-md hover:bg-[#D35400] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#D35400]"
                          title={canTest ? 'Open with the most recent record' : 'No live data yet to test against'}
                        >
                          <Eye size={12} /> Test
                        </button>
                        {r.blank_url && (
                          <a
                            data-testid={`print-blank-${r.key}`}
                            href={`${apiBase}${r.blank_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-600 border border-slate-300 rounded-md hover:bg-slate-700 hover:text-white hover:border-slate-700 transition-all whitespace-nowrap"
                            title="Open a blank (unfilled) version of this form — good for cold-calls / unscheduled inspections"
                          >
                            <FileDown size={12} /> Print Blank
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* RIGHT — Sample uploads */}
              <div className="p-5 space-y-3">
                {(() => {
                  const focused = reportFilter[c.key];
                  const focusedLabel = focused ? (c.built_in.find(r => r.key === focused)?.label || focused) : '';
                  const filteredSamples = focused ? c.samples.filter(s => s.report_key === focused) : c.samples;
                  const selectedInList = filteredSamples.filter(s => selectedSamples[s._id]);
                  const allSelected = filteredSamples.length > 0 && selectedInList.length === filteredSamples.length;
                  return (
                    <>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 truncate">
                            {focused ? (
                              <span className="flex items-center gap-1.5">
                                <Filter size={11} className="text-[#D35400]" />
                                Samples for <span className="text-[#D35400] normal-case tracking-normal">"{focusedLabel}"</span>
                              </span>
                            ) : 'Access database samples'}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {focused && (
                            <button
                              data-testid={`view-all-${c.key}`}
                              onClick={() => setReportFilter(prev => ({ ...prev, [c.key]: '' }))}
                              className="text-[11px] px-2 py-1 rounded border border-[#E2E2DF] text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                            >
                              View all
                            </button>
                          )}
                          {filteredSamples.length > 0 && (
                            <button
                              data-testid={`select-all-${c.key}`}
                              onClick={() => selectAllVisible(filteredSamples)}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#E2E2DF] text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                            >
                              {allSelected ? <CheckSquare size={11} /> : <Square size={11} />}
                              {allSelected ? 'Clear' : 'Select all'}
                            </button>
                          )}
                          {selectedInList.length > 0 && (
                            <button
                              data-testid={`bulk-delete-${c.key}`}
                              onClick={bulkDelete}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                            >
                              <Trash2 size={11} /> Delete {selectedInList.length}
                            </button>
                          )}
                        </div>
                      </div>

                {/* Dropzone */}
                <div
                  data-testid={`dropzone-${c.key}`}
                  onDrop={(e) => onDrop(c.key, e)}
                  onDragOver={onDragOver}
                  className="border-2 border-dashed border-[#E2E2DF] rounded-md p-4 text-center hover:border-[#D35400] transition-colors"
                >
                  <Upload size={20} className="mx-auto text-slate-400 mb-1" />
                  <p className="text-xs text-slate-500">
                    Drag &amp; drop your Access export here, or{' '}
                    <button
                      data-testid={`pick-${c.key}`}
                      onClick={() => fileInputRefs.current[c.key]?.click()}
                      className="text-[#D35400] hover:underline font-semibold"
                    >
                      browse
                    </button>
                    .
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">PDF · Word · RTF · images · Excel · accdb — max 25 MB.</p>
                  {focused && (
                    <p className="text-[10px] text-[#D35400] font-semibold mt-0.5">→ will auto-link to "{focusedLabel}"</p>
                  )}
                  <input
                    ref={el => { fileInputRefs.current[c.key] = el; }}
                    type="file"
                    multiple
                    accept={ACCEPT_TYPES}
                    className="hidden"
                    onChange={(e) => onFilePick(c.key, e)}
                  />
                  {uploadingFor === c.key && (
                    <p className="text-[11px] text-[#D35400] font-semibold mt-1">Uploading…</p>
                  )}
                </div>

                {/* Sample list */}
                {filteredSamples.length === 0 ? (
                  <p className="text-xs italic text-slate-400 text-center py-2">
                    {focused ? 'No samples linked to this report yet.' : 'No samples uploaded yet.'}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {filteredSamples.map(s => (
                      <div key={s._id} data-testid={`sample-${s._id}`} className={`flex items-start gap-2 p-2.5 border rounded-md group ${selectedSamples[s._id] ? 'border-[#D35400] bg-[#D35400]/5' : 'border-[#E2E2DF] hover:border-slate-300'}`}>
                        <button
                          data-testid={`sample-select-${s._id}`}
                          onClick={() => toggleSelect(s._id)}
                          className="flex-shrink-0 mt-0.5 text-slate-400 hover:text-[#D35400]"
                          title="Select for bulk delete"
                        >
                          {selectedSamples[s._id] ? <CheckSquare size={16} className="text-[#D35400]" /> : <Square size={16} />}
                        </button>
                        <FileText size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-800 truncate">{s.filename}</span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{formatBytes(s.size)}</span>
                          </div>
                          {editingId === s._id ? (
                            <div className="mt-1 space-y-1.5">
                              <div className="flex items-center gap-1">
                                <input
                                  data-testid={`sample-notes-input-${s._id}`}
                                  autoFocus
                                  value={editNotes}
                                  onChange={e => setEditNotes(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveSampleEdits(s._id);
                                    if (e.key === 'Escape') { setEditingId(''); setEditNotes(''); setEditStatuses([]); }
                                  }}
                                  placeholder="Add a note (e.g. RTR-Quotation-Letter from MS Access)"
                                  className="flex-1 px-2 py-1 text-xs border border-[#E2E2DF] rounded focus:outline-none focus:ring-1 focus:ring-[#D35400]"
                                />
                                <button data-testid={`sample-notes-save-${s._id}`} onClick={() => saveSampleEdits(s._id)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={14} /></button>
                                <button onClick={() => { setEditingId(''); setEditNotes(''); setEditStatuses([]); }} className="p-1 text-slate-400 hover:bg-slate-50 rounded"><X size={14} /></button>
                              </div>
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1 flex items-center gap-1">
                                  <Tag size={10} /> Statuses
                                </span>
                                {lifecycle.map(opt => {
                                  const on = editStatuses.includes(opt);
                                  return (
                                    <button
                                      key={opt}
                                      type="button"
                                      data-testid={`sample-status-toggle-${s._id}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                                      onClick={() => toggleEditStatus(opt)}
                                      className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${on ? 'bg-[#D35400] text-white border-[#D35400]' : (LIFECYCLE_COLORS[opt] || 'bg-white text-slate-600 border-[#E2E2DF]')}`}
                                    >
                                      {opt}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <>
                              {s.notes ? (
                                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{s.notes}</p>
                              ) : (
                                <p className="text-[11px] text-slate-400 italic mt-0.5">No notes</p>
                              )}
                              {(s.statuses && s.statuses.length > 0) && (
                                <div className="flex items-center gap-1 flex-wrap mt-1">
                                  <Tag size={10} className="text-slate-400" />
                                  {s.statuses.map(v => <StatusChip key={v} value={v} />)}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            data-testid={`sample-view-${s._id}`}
                            href={`${apiBase}/api/reports-library/${s._id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-[#D35400]"
                            title="Open sample"
                          ><ExternalLink size={14} /></a>
                          <button
                            data-testid={`sample-edit-${s._id}`}
                            onClick={() => { setEditingId(s._id); setEditNotes(s.notes || ''); setEditStatuses(s.statuses || []); }}
                            className="p-1.5 text-slate-400 hover:text-slate-700"
                            title="Edit notes & statuses"
                          ><Edit2 size={14} /></button>
                          <button
                            data-testid={`sample-delete-${s._id}`}
                            onClick={() => removeSample(s._id, s.filename)}
                            className="p-1.5 text-slate-400 hover:text-rose-600"
                            title="Delete sample"
                          ><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                    </>
                  );
                })()}
              </div>
            </div>
          </section>
        );
      })}

      {/* Footer help strip */}
      <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-4 text-xs text-slate-600">
        <p className="font-semibold text-slate-700 mb-1">How this page works</p>
        <ul className="list-disc ml-5 space-y-0.5">
          <li><span className="font-semibold">Built-in reports</span> are ready to use today — click <em>Test</em> to open the latest record in the PDF viewer.</li>
          <li><span className="font-semibold">Click any report card</span> to filter the samples panel to just that report's reference files. Click <em>"View all"</em> to see every sample in the category again.</li>
          <li><span className="font-semibold">Status chips</span> indicate the job stage(s) each report applies to — use the top filter to narrow the view to a specific stage.</li>
          <li><span className="font-semibold">Bulk delete</span> samples once they're no longer needed for reference: tick the boxes (or <em>Select all</em>) and hit <em>Delete</em>.</li>
          <li>Samples can be any file: PDF, Word, RTF, images, Excel, or even raw Access (.accdb/.mdb) — auto-linked to the focused report when you upload while one is selected.</li>
        </ul>
      </div>
    </div>
  );
}
